import { Result } from 'ant-design-vue';
import { RootRoute } from '@/router/routes';
import RouterView from '@/layouts/page/index.vue';
import MicroContainer from '@/micro/index.vue';
import { isHttpUrl as isUrl } from '@/utils/is';
import { uniqueSlash } from '@/fantasy/utils/urlUtils';
import { constantRouterComponents } from '@/fantasy/router/asyncModules';
import { resetRouter, router } from '@/router';
import { Menu, AppRouteRecordRaw } from '@/router/types';
import NotFound from '@/views/sys/exception/Exception.vue';
import { LAYOUT } from '@/router/constant';
import { MD5 } from 'crypto-js';

function isNestedDir(path) {
  return path?.split('/').length > 2;
}
export function filterAsyncRoute(
  routes: API.Menu[],
  parentRoute: API.Menu | null = null,
  lastNamePath: string[] = [],
): AppRouteRecordRaw[] {
  return routes
    .filter(
      (item) =>
        item.type !== 2 &&
        // item.isShow &&
        item.parentId == (parentRoute?.id ?? -1),
    )
    .map((item) => {
      const { router, viewPath, name, icon, orderNum, keepalive, isShow, isUniapp } = item;
      let fullPath = '';
      const pathPrefix = lastNamePath.slice(-1)[0] || '';
      if (isUrl(router)) {
        fullPath = router;
      } else {
        fullPath = router.startsWith('/') ? router : `/${router}`;
        fullPath = router.startsWith(pathPrefix) ? fullPath : pathPrefix + fullPath;
        if (!isUniapp) {
          // 不知道这一步有啥用
          fullPath = [...new Set(uniqueSlash(fullPath).split('/'))].join('/');
        }
      }
      let realRoutePath = router;
      if (parentRoute) {
        if (fullPath.startsWith(parentRoute?.router)) {
          realRoutePath = fullPath.split(parentRoute.router)[1];
        } else if (!isUrl(parentRoute.router) && !isUrl(router)) {
          realRoutePath = router;
        }
      }
      realRoutePath = realRoutePath.startsWith('/') ? realRoutePath.slice(1) : realRoutePath;
      const route: Partial<AppRouteRecordRaw> = {
        path: isUrl(realRoutePath) ? `/iframe/${MD5(fullPath)}` : realRoutePath,
        // name: `${viewPath ? toHump(viewPath) : fullPath}-${item.id}`,
        name: isUrl(fullPath) ? `/iframe/${MD5(fullPath)}` : fullPath,
        meta: {
          orderNum,
          title: name,
          type: item.type,
          perms: [],
          icon,
          namePath: lastNamePath.concat(fullPath),
          keepAlive: keepalive,
          isShow,
          isUniapp: item.isUniapp,
          isMicroPage: item.isMicroPage,
          frameSrc: isUrl(realRoutePath) ? realRoutePath : '',
        },
      };

      if (item.type === 0) {
        // 如果是目录
        const children = filterAsyncRoute(routes, item, lastNamePath.concat(fullPath));
        if (children?.length) {
          route.component = isNestedDir(item.router) || item.solo ? RouterView : LAYOUT;
          route.children = children;
          route.redirect = { name: children[0].name };
        } else {
          route.component = (
            <Result
              status="500"
              title={name}
              sub-title="目录类型菜单不是真实页面，请为当前目录添加页面级子菜单或更改当前菜单类型."
            />
          );
        }
        return route;
      } else if (item.type === 1) {
        // 如果是页面
        const Component = constantRouterComponents[viewPath] || NotFound;

        if (Component === NotFound) {
          console.warn(item.name + '找不到页面');
        }
        route.component = Component;

        const perms = routes
          .filter((n) => n.parentId === item.id)
          .flatMap((n) => n.perms?.split(','));
        if (route.meta && perms) {
          // 设置当前页面所拥有的权限
          route.meta.perms = perms;
        }
        return route;
      }
      return undefined;
    })
    .filter((item): item is AppRouteRecordRaw => !!item);
}

function normalizeMenus(menus?: AppRouteRecordRaw[], result: Menu[] = []) {
  if (menus && menus.length)
    menus.forEach((menu) => {
      const item: Menu = {
        path: menu.name,
        icon: menu.meta?.icon,
        name: menu.path,
        // paramPath: menu.meta?.title,
        disabled: !!menu.meta?.disabled,
        children: normalizeMenus(menu.children),
        orderNo: menu.meta?.orderNo,
        roles: menu.meta?.roles,
        meta: {
          hideMenu: !menu.meta?.isShow,
          title: menu.meta?.title,
          isUniapp: menu.meta?.isUniapp,
          isMicroPage: menu.meta?.isMicroPage
        },
        // tag: menu.meta?.tag,
        hideMenu: !menu.meta?.isShow,
        alwaysShow: !!menu.meta?.alwaysShow,
      };
      result.push(item);
    });
  return result;
}

/**
 * 动态生成菜单
 * @param token
 * @returns {Promise<Router>}
 */
export const generatorDynamicRouter = (asyncMenus: API.Menu[]) => {
  try {
    // console.log('asyncMenus', asyncMenus);
    const routeList = filterAsyncRoute(asyncMenus);
    // console.log(routeList, '根据后端返回的权限路由生成');
    // 重置路由
    resetRouter();
    const menus = [...routeList];
    RootRoute.children = menus;
    // 重新添加拍平后的路由
    // @ts-ignore
    router.addRoute(RootRoute);
    console.log('所有路由', router.getRoutes());

    return {
      menus: normalizeMenus(menus),
      routes: RootRoute.children,
    };
  } catch (error) {
    console.error('生成路由时出错', error);
    return {
      menus: [],
      routes: [],
    };
  }
};

/**
 * 主要方便于控制a-menu的open-keys，即控制左侧菜单应当展开哪些菜单
 * @param {AppRouteRecordRaw[]} routes 需要添加namePath的路由
 * @param {string[]} namePath
 */
export const generatorNamePath = (
  routes: AppRouteRecordRaw[],
  namePath?: string[],
  parent?: AppRouteRecordRaw,
) => {
  routes.forEach((item) => {
    if (item.children?.length) {
      if (item.meta && typeof item.name === 'string') {
        item.meta.namePath = Array.isArray(namePath) ? namePath.concat(item.name) : [item.name];
        item.meta.fullPath = parent?.meta?.fullPath
          ? [parent.meta.fullPath, item.path].join('/')
          : item.path;
        item.meta.fullPath = uniqueSlash(item.meta.fullPath as string);
        generatorNamePath(item.children, item.meta.namePath as string[], item);
      }
    } else {
      if (item.meta && typeof item.name === 'string') {
        item.meta.namePath = Array.isArray(namePath) ? namePath.concat(item.name) : [item.name];
        item.meta.fullPath = parent?.meta?.fullPath
          ? [parent.meta.fullPath, item.path].join('/')
          : item.path;
        item.meta.fullPath = uniqueSlash(item.meta.fullPath as string);
      }
    }
  });
};
